import { computed, defineComponent, h, inject } from '@vue/composition-api';
import { HandleElement } from '../../types';
import { ConnectionLineType, ConnectionMode, Position } from '../../types';
import { getHandlePosition, getMarkerId, oppositePosition } from '../../utils';
import { useWorkFlow } from '../../composables';
import {
  getBezierPath,
  getSimpleBezierPath,
  getSmoothStepPath
} from '../edge/utils';

import { Slots } from '../../context';

const ConnectionLine = defineComponent({
  name: 'ConnectionLine',
  setup() {
    const {
      id,
      connectionMode,
      connectionStartHandle,
      connectionEndHandle,
      connectionPosition,
      connectionLineType,
      connectionLineStyle,
      connectionLineOptions,
      connectionStatus,
      viewport,
      findNode,
      disabled
    } = useWorkFlow();

    const connectionLineComponent = inject(Slots)?.['connection-line'];

    const fromNode = computed(() =>
      findNode(connectionStartHandle.value?.nodeId)
    );

    const toNode = computed(
      () => findNode(connectionEndHandle.value?.nodeId) ?? null
    );

    const toXY = computed(() => {
      return {
        x:
          (connectionPosition.value.x - viewport.value.x) / viewport.value.zoom,
        y: (connectionPosition.value.y - viewport.value.y) / viewport.value.zoom
      };
    });

    const markerStart = computed(() =>
      connectionLineOptions.value.markerStart
        ? `url(#${getMarkerId(connectionLineOptions.value.markerStart, id)})`
        : ''
    );

    const markerEnd = computed(() =>
      connectionLineOptions.value.markerEnd
        ? `url(#${getMarkerId(connectionLineOptions.value.markerEnd, id)})`
        : ''
    );

    const noConnectionLineRender = h('svg', {
      class: 'workflow__edges workflow__connectionline workflow__container'
    });

    return () => {
      if (disabled.value) {
        return noConnectionLineRender;
      }

      if (!fromNode.value || !connectionStartHandle.value) {
        return noConnectionLineRender;
      }

      const startHandleId = connectionStartHandle.value.id;

      const handleType = connectionStartHandle.value.type;

      const fromHandleBounds = fromNode.value.handleBounds;
      let handleBounds = fromHandleBounds?.[handleType] || [];

      if (connectionMode.value === ConnectionMode.Loose) {
        const oppositeBounds =
          fromHandleBounds?.[handleType === 'source' ? 'target' : 'source'] ||
          [];
        handleBounds = [...handleBounds, ...oppositeBounds];
      }

      if (!handleBounds) {
        return noConnectionLineRender;
      }

      const fromHandle =
        (startHandleId
          ? handleBounds.find(d => d.id === startHandleId)
          : handleBounds[0]) ?? null;
      const fromPosition = fromHandle?.position || Position.Top;
      const { x: fromX, y: fromY } = getHandlePosition(
        fromNode.value,
        fromHandle,
        fromPosition
      );

      let toHandle: HandleElement | null = null;
      if (toNode.value) {
        // if connection mode is strict, we only look for handles of the opposite type
        if (connectionMode.value === ConnectionMode.Strict) {
          toHandle =
            toNode.value.handleBounds[
              handleType === 'source' ? 'target' : 'source'
            ]?.find(d => d.id === connectionEndHandle.value?.id) || null;
        } else {
          // if connection mode is loose, look for the handle in both source and target bounds
          toHandle =
            [
              ...(toNode.value.handleBounds.source || []),
              ...(toNode.value.handleBounds.target || [])
            ]?.find(d => d.id === connectionEndHandle.value?.id) || null;
        }
      }

      const toPosition =
        connectionEndHandle.value?.position ??
        (fromPosition ? oppositePosition[fromPosition] : null);

      if (!fromPosition || !toPosition) {
        return noConnectionLineRender;
      }

      const type =
        connectionLineType.value ??
        connectionLineOptions.value.type ??
        ConnectionLineType.Bezier;

      let dAttr = '';

      const pathParams = {
        sourceX: fromX,
        sourceY: fromY,
        sourcePosition: fromPosition,
        targetX: toXY.value.x,
        targetY: toXY.value.y,
        targetPosition: toPosition
      };

      if (type === ConnectionLineType.Bezier) {
        [dAttr] = getBezierPath(pathParams);
      } else if (type === ConnectionLineType.Step) {
        [dAttr] = getSmoothStepPath({
          ...pathParams,
          borderRadius: 0
        });
      } else if (type === ConnectionLineType.SmoothStep) {
        [dAttr] = getSmoothStepPath(pathParams);
      } else if (type === ConnectionLineType.SimpleBezier) {
        [dAttr] = getSimpleBezierPath(pathParams);
      } else {
        dAttr = `M${fromX},${fromY} ${toXY.value.x},${toXY.value.y}`;
      }

      return h(
        'svg',
        {
          class: 'workflow__edges workflow__connectionline workflow__container'
        },
        [
          connectionLineComponent
            ? h('component', {
                props: {
                  is: connectionLineComponent,
                  sourceX: fromX,
                  sourceY: fromY,
                  sourcePosition: fromPosition,
                  targetX: toXY.value.x,
                  targetY: toXY.value.y,
                  targetPosition: toPosition,
                  sourceNode: fromNode.value,
                  sourceHandle: fromHandle,
                  targetNode: toNode.value,
                  targetHandle: toHandle,
                  markerEnd: markerEnd.value,
                  markerStart: markerStart.value,
                  connectionStatus: connectionStatus.value
                }
              })
            : h('g', { class: 'workflow__connection' }, [
                h('path', {
                  attrs: {
                    d: dAttr,
                    'marker-end': markerEnd.value,
                    'marker-start': markerStart.value
                  },
                  class: [
                    connectionLineOptions.value.class,
                    connectionStatus.value,
                    'workflow__connection-path'
                  ],
                  style: {
                    ...connectionLineStyle.value,
                    ...connectionLineOptions.value.style
                  }
                })
              ])
        ]
      );
    };
  }
});

export default ConnectionLine;
